<script setup>
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue';
import SalesInsightCard from './SalesInsightCard.vue';
import { useFlowieHandoff } from '@/composables/useFlowieHandoff'

const props = defineProps({
    leadId: {
        type: [Number, null],
        default: null,
    },
    domain: {
        type: String,
        default: null,
    },
    insightsLoaded: {
        type: Boolean,
        default: false,
    },
    initialMessage: {
        type: String,
        default: '',
    },
});

const messages = ref([]);
const isGenerating = ref(false);
const conversationId = ref(null);
const chatInput = ref('');
const chatContainer = ref(null);
const pollTimer = ref(null);

const { flowieComposerVisible, closeFlowieComposer } = useFlowieHandoff(
    () => props.initialMessage,
    chatInput
);
const pollInterval = ref(1000);
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);
const progressWidth = ref(0);
const progressInterval = ref(null);
const hasError = ref(false);
const errorMessage = ref('');

const isStandalone = computed(() => props.leadId === null);

const funMessages = [
    { icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z', text: () => _mft('crm:nina.generating.analyzing') },
    { icon: 'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25', text: () => _mft('crm:nina.generating.researching') },
    { icon: 'M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z', text: () => _mft('crm:nina.generating.crafting') },
    { icon: 'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.745 3.745 0 011.043 3.296A3.745 3.745 0 0121 12z', text: () => _mft('crm:nina.generating.finishing') },
];

const currentFunMessage = computed(() => funMessages[funMessageIndex.value % funMessages.length]);

const startFunMessages = () => {
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;

    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value = (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);

    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopFunMessages = () => {
    if (funMessageInterval.value) {
        clearInterval(funMessageInterval.value);
        funMessageInterval.value = null;
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
    progressWidth.value = 100;
};

const scrollToBottom = () => {
    nextTick(() => {
        if (chatContainer.value) {
            chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
        }
    });
};

watch(() => messages.value.length, scrollToBottom);
watch(() => isGenerating.value, (val) => {
    if (val) startFunMessages();
    else stopFunMessages();
    scrollToBottom();
});

const renderMarkdown = (text) => {
    if (!text) return '';
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li class="ml-4 list-decimal">$2</li>');
    html = html.replace(/^[-•]\s+(.+)$/gm, '<li class="ml-4 list-disc">$1</li>');
    html = html.replace(/\n\n/g, '</p><p class="mt-2">');
    html = html.replace(/\n/g, '<br>');

    return '<p>' + html + '</p>';
};

const loadConversation = async () => {
    try {
        const res = isStandalone.value
            ? await axios.get(route('nina.standalone.conversation'), { params: { domain: props.domain } })
            : await axios.get(route('nina.conversation', props.leadId));
        if (res.data.id) {
            conversationId.value = res.data.id;
            messages.value = res.data.messages || [];
        }
    } catch (e) {
        // No existing conversation, that's fine
    }
};

const sendMessage = async (text) => {
    const message = text || chatInput.value.trim();
    if (!message || isGenerating.value) return;
    chatInput.value = '';
    hasError.value = false;
    errorMessage.value = '';

    try {
        const res = isStandalone.value
            ? await axios.post(route('nina.standalone.generate'), {
                prompt: message,
                domain: props.domain,
            })
            : await axios.post(route('nina.generate', props.leadId), {
                prompt: message,
                domain: props.domain,
            });

        conversationId.value = res.data.conversation_id;

        // Optimistically add user message
        messages.value.push({ role: 'user', content: message });

        if (res.data.status === 'budget_exceeded') {
            pushBudgetExceededMessage();
            return;
        }

        isGenerating.value = true;
        startPolling(res.data.conversation_id);
    } catch (e) {
        hasError.value = true;
        if (e.response?.status === 403 && e.response?.data?.error === 'specialist_not_hired') {
            errorMessage.value = _mft('crm:nina.error.notHired');
        } else if (e.response?.status === 422) {
            errorMessage.value = _mft('crm:nina.error.validation');
        } else {
            errorMessage.value = _mft('crm:nina.error.generic');
        }
    }
};

// Nina's reply when there is no AI usage left. It only lives here: the server
// never stores it, so it is gone on reload and never reaches Nina's history.
const pushBudgetExceededMessage = () => {
    messages.value.push({ role: 'assistant', content: _mft('crm:nina.error.budgetExceeded'), budgetExceeded: true });
};

const startPolling = (convId) => {
    pollInterval.value = 1000;
    stopPolling();

    const poll = async () => {
        try {
            const res = await axios.get(route('nina.poll', convId));
            const data = res.data;

            if (data.status === 'completed') {
                isGenerating.value = false;
                stopPolling();

                // Remove the optimistic user message and reload full conversation
                await loadConversation();
                return;
            }

            if (data.status === 'budget_exceeded') {
                isGenerating.value = false;
                stopPolling();
                pushBudgetExceededMessage();
                return;
            }

            if (data.status === 'failed') {
                isGenerating.value = false;
                stopPolling();
                hasError.value = true;
                errorMessage.value = _mft('crm:nina.error.generic');

                // Remove optimistic user message on failure
                if (messages.value.length > 0 && messages.value[messages.value.length - 1].role === 'user') {
                    messages.value.pop();
                }
                return;
            }

            // Adaptive polling: 1s → 3s
            pollInterval.value = Math.min(pollInterval.value + 500, 3000);
            pollTimer.value = setTimeout(poll, pollInterval.value);
        } catch (e) {
            isGenerating.value = false;
            stopPolling();
        }
    };

    pollTimer.value = setTimeout(poll, pollInterval.value);
};

const stopPolling = () => {
    if (pollTimer.value) {
        clearTimeout(pollTimer.value);
        pollTimer.value = null;
    }
};

const newConversation = async () => {
    try {
        const res = isStandalone.value
            ? await axios.post(route('nina.standalone.new'), { domain: props.domain })
            : await axios.post(route('nina.new', props.leadId));
        conversationId.value = res.data.id;
        messages.value = [];
        hasError.value = false;
        errorMessage.value = '';
    } catch (e) {
        // Silently fail
    }
};

const handleKeydown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
    }
};

onMounted(() => {
    loadConversation();
});

onUnmounted(() => {
    stopPolling();
    stopFunMessages();
});
</script>

<template>
    <div class="flex flex-col h-full">
        <flowie-handoff-composer
            v-model="chatInput"
            :visible="flowieComposerVisible"
            :disabled="isGenerating"
            :placeholder="_mft('crm:nina.inputPlaceholder')"
            @close="closeFlowieComposer"
            @send="sendMessage"
        />

        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div class="flex items-center gap-2">
                <div class="flex items-center justify-center w-8 h-8 rounded-full bg-aiAgentSalesAssistant">
                    <svg class="w-4 h-4 text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                </div>
                <span class="text-sm font-medium text-gray-700">Closer</span>
            </div>
            <button-secondary
                v-if="messages.length > 0"
                @click="newConversation"
                size="sm"
            >
                <mf-translate domainkey="crm:nina.newChat"></mf-translate>
            </button-secondary>
        </div>

        <!-- Messages -->
        <div ref="chatContainer" class="flex-1 overflow-y-auto p-4 min-h-[300px] max-h-[60vh]">
            <div class="flex min-h-full flex-col justify-end">
                <!-- Welcome state -->
                <div v-if="messages.length === 0 && !isGenerating" class="flex flex-col items-center justify-center py-8 text-center">
                    <div class="flex items-center justify-center w-14 h-14 mb-4 rounded-full bg-aiAgentSalesAssistant">
                        <svg class="w-7 h-7 text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                        </svg>
                    </div>
                    <p class="mb-1 text-sm text-gray-500">
                        <mf-translate domainkey="crm:nina.welcomeMessage"></mf-translate>
                    </p>
                    <div class="flex flex-wrap gap-2 mt-3 justify-center">
                        <button-secondary
                            @click="sendMessage(_mft('crm:nina.prompt.challenges'))"
                            size="sm"
                        >
                            <mf-translate domainkey="crm:nina.prompt.challenges"></mf-translate>
                        </button-secondary>
                        <button-secondary
                            @click="sendMessage(_mft('crm:nina.prompt.talkingPoints'))"
                            size="sm"
                        >
                            <mf-translate domainkey="crm:nina.prompt.talkingPoints"></mf-translate>
                        </button-secondary>
                        <button-secondary
                            @click="sendMessage(_mft('crm:nina.prompt.salesStrategy'))"
                            size="sm"
                        >
                            <mf-translate domainkey="crm:nina.prompt.salesStrategy"></mf-translate>
                        </button-secondary>
                    </div>
                </div>

                <!-- Chat messages -->
                <template v-for="(msg, i) in messages" :key="i">
                    <div class="flex gap-3 mb-3" :class="{ 'justify-end': msg.role === 'user' }">
                        <!-- Nina avatar -->
                        <div v-if="msg.role === 'assistant'" class="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-aiAgentSalesAssistant">
                            <svg class="w-4 h-4 text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                            </svg>
                        </div>

                        <div
                            class="px-4 py-3 rounded-lg max-w-[85%]"
                            :class="msg.role === 'user' ? 'bg-aiAgentSalesAssistant text-gray-900' : 'bg-gray-50 text-gray-700'"
                        >
                            <div v-if="msg.role === 'assistant'" class="text-sm" v-html="renderMarkdown(msg.content)"></div>
                            <p v-else class="text-sm">{{ msg.content }}</p>

                            <utility-ai-usage-cta v-if="msg.budgetExceeded" class="mt-3" />

                            <!-- Sales insight card -->
                            <sales-insight-card
                                v-if="msg.type === 'sales_insight' && (msg.recommendations?.length || msg.talking_points?.length)"
                                :recommendations="msg.recommendations || []"
                                :talking-points="msg.talking_points || []"
                            />
                        </div>
                    </div>
                </template>

                <!-- Generating animation -->
                <div v-if="isGenerating" class="flex justify-start mb-3 generating-appear">
                    <div class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50">
                        <div class="h-1 w-full bg-gray-200 overflow-hidden">
                            <div
                                class="h-full bg-aiAgentSalesAssistant transition-all duration-700 ease-out"
                                :style="{ width: progressWidth + '%' }"
                            ></div>
                        </div>
                        <div class="px-5 py-4">
                            <div class="flex items-center gap-3 mb-3">
                                <div class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg bg-aiAgentSalesAssistant flex items-center justify-center" :key="funMessageIndex">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" :d="currentFunMessage.icon" />
                                    </svg>
                                </div>
                                <span class="text-sm font-medium text-gray-700 generating-text" :key="'t' + funMessageIndex">{{ currentFunMessage.text() }}</span>
                            </div>
                            <div class="space-y-2">
                                <div class="flex gap-2">
                                    <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 65%"></div>
                                    <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 25%; animation-delay: 0.3s"></div>
                                </div>
                                <div class="flex gap-2">
                                    <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 40%; animation-delay: 0.15s"></div>
                                    <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 50%; animation-delay: 0.45s"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Error message -->
                <div v-if="hasError" class="flex justify-center mb-3">
                    <div class="px-4 py-2 text-sm text-red-600 bg-red-50 rounded-lg">
                        {{ errorMessage }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Input -->
        <div class="p-4 border-t border-gray-100">
            <div class="flex gap-2">
                <input
                    v-model="chatInput"
                    type="text"
                    class="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aiAgentSalesAssistant focus:border-aiAgentSalesAssistant"
                    :placeholder="_mft('crm:nina.inputPlaceholder')"
                    :disabled="isGenerating"
                    @keydown="handleKeydown"
                />
                <button-base
                    @click="sendMessage()"
                    style-type="primary"
                    :disabled="isGenerating"
                    size="sm"
                    class="!px-4"
                >
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                        </svg>
                    </template>
                </button-base>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes skeleton-shimmer {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
}
.skeleton-shimmer {
    animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes generating-appear {
    from { opacity: 0; transform: translateY(8px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}
.generating-appear {
    animation: generating-appear 0.4s ease-out;
}

@keyframes icon-pop {
    0% { transform: scale(0) rotate(-20deg); }
    60% { transform: scale(1.15) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
}
.generating-icon {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slide {
    from { opacity: 0; transform: translateX(-8px); }
    to { opacity: 1; transform: translateX(0); }
}
.generating-text {
    animation: text-slide 0.4s ease-out;
}
</style>
